import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi, tokenStore, ApiError } from '@/lib/api'
import { useIsAuthenticated, useAppStore } from '@/store'

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: 'Google sign-in failed. Please try again.',
  access_denied: 'Sign-in was cancelled. Please try again.',
}

// Google and Apple brand icons as inline SVG
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" fill="currentColor" aria-hidden="true">
      <path d="M13.174 9.382c-.013-1.653.748-2.906 2.286-3.827-.86-1.225-2.156-1.9-3.873-2.028-1.63-.126-3.408.955-4.057.955-.686 0-2.27-.908-3.52-.908C1.535 3.617-.034 5.29.001 8.686c.05 4.74 3.006 9.314 4.936 9.314.895 0 1.59-.604 3.025-.604 1.393 0 1.979.595 3.014.595 1.951 0 3.72-4.291 3.924-6.005-1.726-.813-1.726-2.604-1.726-2.604zM10.596 2.098C11.437.949 11.332-.3 11.297-.5c-.987.06-2.14.677-2.839 1.518-.645.776-1.163 1.97-.996 3.13 1.077.083 2.292-.52 3.134-2.05z"/>
    </svg>
  )
}

const MANDALA_SYMBOLS = ['☿', '♀', '♂', '♃', '♄', '♅', '♆', '☉', '☽']

const RESEND_COOLDOWN_SEC = 60

export function LoginPage() {
  const isAuthenticated = useIsAuthenticated()
  const { setUser } = useAppStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const errorCode = searchParams.get('error')
  const oauthErrorMessage = errorCode
    ? (AUTH_ERROR_MESSAGES[errorCode] ?? 'Sign-in failed. Please try again.')
    : null

  // Email OTP state
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const otpInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true })
  }, [isAuthenticated, navigate])

  // Handle OAuth callback: Google/Apple redirect back to /login#access_token=...&refresh_token=...
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.includes('access_token=')) return
    const params = new URLSearchParams(hash.slice(1))
    const accessToken = params.get('access_token')
    const refreshToken = params.get('refresh_token')
    if (!accessToken || !refreshToken) return

    window.history.replaceState(null, '', window.location.pathname)
    tokenStore.set(accessToken)
    tokenStore.setRefresh(refreshToken)
    authApi.me().then((user) => {
      setUser(user)
      const needsOnboarding = !user.profile?.dateOfBirth
      navigate(needsOnboarding ? '/onboarding' : '/dashboard', { replace: true })
    }).catch(() => {
      tokenStore.clear()
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Focus OTP input when step changes
  useEffect(() => {
    if (step === 'otp') otpInputRef.current?.focus()
  }, [step])

  // Cooldown countdown
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  function startCooldown() {
    setResendCooldown(RESEND_COOLDOWN_SEC)
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownRef.current!)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setFieldError(null)
    setLoading(true)
    try {
      await authApi.sendEmailOtp(email.trim())
      setStep('otp')
      setOtp('')
      startCooldown()
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 429) {
        setFieldError('Too many requests. Please wait a few minutes before requesting another code.')
      } else {
        setFieldError('Failed to send code. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault()
    if (otp.length !== 6) return
    setFieldError(null)
    setLoading(true)
    try {
      const { accessToken, refreshToken } = await authApi.verifyEmailOtp(email.trim(), otp.trim())
      tokenStore.set(accessToken)
      tokenStore.setRefresh(refreshToken)
      const user = await authApi.me()
      setUser(user)
      const needsOnboarding = !user.profile?.dateOfBirth
      navigate(needsOnboarding ? '/onboarding' : '/dashboard', { replace: true })
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 400) {
        setFieldError('Invalid or expired code. Please try again.')
      } else {
        setFieldError('Verification failed. Please try again.')
      }
      setOtp('')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || loading) return
    setFieldError(null)
    setLoading(true)
    try {
      await authApi.sendEmailOtp(email.trim())
      setOtp('')
      startCooldown()
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 429) {
        setFieldError('Too many requests. Please wait a few minutes before trying again.')
      } else {
        setFieldError('Failed to resend code. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  function handleOtpChange(value: string) {
    // Only allow digits, max 6
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setOtp(digits)
    setFieldError(null)
  }

  return (
    <div className="min-h-screen bg-cosmic-void flex flex-col items-center justify-center relative overflow-hidden">

      {/* Background layers */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 70% 60% at 50% 0%, rgba(107,33,168,0.2) 0%, transparent 70%),
            radial-gradient(ellipse 50% 50% at 80% 100%, rgba(59,7,100,0.3) 0%, transparent 60%)
          `,
        }}
        aria-hidden="true"
      />

      {/* Floating planetary symbols — gently twinkle in the background */}
      {MANDALA_SYMBOLS.map((sym, i) => (
        <span
          key={sym}
          className="absolute text-star-dim/25 font-mono select-none pointer-events-none animate-twinkle"
          style={{
            left: `${10 + i * 10}%`,
            top: `${15 + (i % 3) * 25}%`,
            fontSize: `${14 + (i % 3) * 6}px`,
            animationDelay: `${i * 0.7}s`,
            animationDuration: `${3 + i * 0.5}s`,
          }}
          aria-hidden="true"
        >
          {sym}
        </span>
      ))}

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-deep to-violet-mid mb-4 shadow-glow-violet">
            <span className="text-gold text-2xl text-glow-gold">✦</span>
          </div>
          <h1 className="font-display text-3xl font-light text-star-white tracking-wide">
            Shubh Time
          </h1>
          <p className="text-star-muted text-sm mt-1.5 font-mono tracking-widest">
            शुभ समय • Muhurta as a Service
          </p>
        </div>

        {/* Login card */}
        <div className="card-cosmic p-6 space-y-4">
          <div className="text-center mb-2">
            <p className="text-star-soft text-sm">
              {step === 'otp'
                ? `Enter the 6-digit code sent to ${email}`
                : 'Sign in to access your cosmic guidance'}
            </p>
          </div>

          {/* OAuth error banner (only on email step) */}
          {step === 'email' && oauthErrorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-red-950/60 border border-red-800/60 px-3 py-2.5 text-sm text-red-300"
            >
              <span aria-hidden="true" className="mt-0.5 shrink-0">⚠</span>
              <span>{oauthErrorMessage}</span>
            </div>
          )}

          {/* Field error banner */}
          {fieldError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-red-950/60 border border-red-800/60 px-3 py-2.5 text-sm text-red-300"
            >
              <span aria-hidden="true" className="mt-0.5 shrink-0">⚠</span>
              <span>{fieldError}</span>
            </div>
          )}

          {step === 'email' ? (
            <>
              {/* Email OTP form */}
              <form onSubmit={handleSendOtp} className="space-y-3">
                <div>
                  <label htmlFor="email-input" className="sr-only">Email address</label>
                  <input
                    id="email-input"
                    type="email"
                    autoComplete="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setFieldError(null) }}
                    required
                    disabled={loading}
                    className="w-full px-4 py-2.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist
                               text-star-bright text-sm placeholder:text-star-dim
                               focus:outline-none focus:border-violet-mid focus:ring-1 focus:ring-violet-mid/50
                               disabled:opacity-50 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                             bg-violet-deep border border-violet-mid text-star-bright text-sm font-medium
                             hover:bg-violet-mid hover:border-violet-bright
                             active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
                             transition-all duration-150"
                >
                  {loading ? (
                    <span className="inline-block w-4 h-4 border-2 border-star-muted border-t-star-bright rounded-full animate-spin" aria-hidden="true" />
                  ) : null}
                  Continue with Email
                </button>
              </form>

              {/* Divider */}
              <div className="divider-cosmic text-star-dim text-2xs font-mono">
                <span>or</span>
              </div>

              {/* Google */}
              <button
                onClick={() => authApi.googleLogin()}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg
                           bg-cosmic-dusk border border-cosmic-mist text-star-bright text-sm font-medium
                           hover:bg-cosmic-aurora hover:border-violet-dark
                           active:scale-[0.98] transition-all duration-150"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              {/* Apple */}
              <button
                onClick={() => authApi.appleLogin()}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg
                           bg-cosmic-dusk border border-cosmic-mist text-star-bright text-sm font-medium
                           hover:bg-cosmic-aurora hover:border-violet-dark
                           active:scale-[0.98] transition-all duration-150"
              >
                <AppleIcon />
                Continue with Apple
              </button>
            </>
          ) : (
            /* OTP verification step */
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <div>
                <label htmlFor="otp-input" className="sr-only">6-digit verification code</label>
                <input
                  ref={otpInputRef}
                  id="otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  maxLength={6}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2.5 rounded-lg bg-cosmic-dusk border border-cosmic-mist
                             text-star-bright text-sm text-center tracking-[0.4em] placeholder:tracking-normal placeholder:text-star-dim
                             focus:outline-none focus:border-violet-mid focus:ring-1 focus:ring-violet-mid/50
                             disabled:opacity-50 transition-colors font-mono"
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                           bg-violet-deep border border-violet-mid text-star-bright text-sm font-medium
                           hover:bg-violet-mid hover:border-violet-bright
                           active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-150"
              >
                {loading ? (
                  <span className="inline-block w-4 h-4 border-2 border-star-muted border-t-star-bright rounded-full animate-spin" aria-hidden="true" />
                ) : null}
                Verify Code
              </button>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => { setStep('email'); setOtp(''); setFieldError(null) }}
                  className="text-2xs text-star-dim hover:text-star-soft transition-colors"
                >
                  ← Change email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  className="text-2xs text-gold/70 hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {/* Terms */}
          <p className="text-center text-2xs text-star-dim leading-relaxed pt-2">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-gold/70 hover:text-gold transition-colors">Terms</a>
            {' '}and{' '}
            <a href="/privacy" className="text-gold/70 hover:text-gold transition-colors">Privacy Policy</a>
          </p>
        </div>

        {/* Tagline */}
        <p className="text-center text-star-dim text-2xs mt-6 font-display italic">
          "Kālasya kūṭasthasya tu ko'nuvartet" — Who follows the immovable time?
        </p>
      </div>
    </div>
  )
}
